import React from 'react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const PrivacyPolicy: React.FC = () => {
  // SEO optimization for Privacy Policy page
  useDocumentTitle({
    title: 'Privacy Policy - WageWise | H1B Visa Job Search Platform',
    description: 'Read WageWise privacy policy to understand how we collect, use, and protect your personal information when using our H1B visa job search platform.',
    keywords: 'privacy policy, data protection, WageWise, H1B visa, job search, personal information',
    canonicalUrl: 'https://wagewise.app/privacy',
    noIndex: false
  });

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Privacy Policy</h1>
        <p className="text-gray-600">Learn how WageWise protects your privacy and handles your data</p>
      </header>
      
      <div className="prose prose-gray max-w-none">
        <p className="mb-6 text-sm text-gray-500">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Introduction</h2>
          <p className="mb-4">
            WageWise ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and share information about you when you use our website and services for H1B visa job search and sponsorship information.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Information We Collect</h2>
          <p className="mb-3">We collect information you provide directly to us, such as:</p>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Account information (name, email address, password)</li>
            <li>Profile information (job title, employment history)</li>
            <li>Search queries and preferences for H1B sponsorship jobs</li>
            <li>Communications you send to us</li>
          </ul>
          <p className="mb-4">
            We also automatically collect certain information when you visit our website, including IP address, browser type, device information, and usage data to improve our H1B job search services.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">How We Use Your Information</h2>
          <p className="mb-3">We use the information we collect to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Provide, maintain, and improve our H1B job search services</li>
            <li>Process and complete job searches and applications</li>
            <li>Communicate with you about our services and H1B sponsorship opportunities</li>
            <li>Personalize your job search experience</li>
            <li>Monitor and analyze usage patterns to improve our platform</li>
            <li>Protect against fraudulent or unauthorized activity</li>
          </ul>
        </section>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">How We Share Information</h2>
          <p className="mb-3">
            We may share information about you in the following circumstances:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>With employers when you apply for jobs through our service</li>
            <li>With service providers who need access to perform services on our behalf</li>
            <li>If required by law, regulation, or legal process</li>
            <li>To protect the rights, safety, and security of WageWise, our users, or the public</li>
            <li>In connection with a merger, acquisition, or sale of assets</li>
          </ul>
        </section>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Data Security</h2>
          <p className="mb-4">
            We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no internet transmission is completely secure, and we cannot guarantee absolute security.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Your Rights and Choices</h2>
          <p className="mb-3">
            You can access and update certain information about you through your account settings.
            You can also opt out of receiving promotional communications from us by following the instructions in those messages.
          </p>
          <p className="mb-3">Depending on your location, you may have additional rights including:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Right to access your personal information</li>
            <li>Right to correct inaccurate information</li>
            <li>Right to delete your personal information</li>
            <li>Right to restrict processing of your information</li>
            <li>Right to data portability</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Cookies and Tracking</h2>
          <p className="mb-4">
            We use cookies and similar technologies to enhance your browsing experience, analyze site traffic, and personalize content. You can control cookie preferences through your browser settings.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Changes to This Policy</h2>
          <p className="mb-4">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our privacy practices, please contact us through our <a href="/contact" className="text-blue-600 hover:text-blue-800 underline">contact page</a>.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 